// Native Google Sign-In wrapper for Capacitor (Android/iOS).
//
// On the web we use Authlib's OAuth redirect flow. On native we use the
// @codetrix-studio/capacitor-google-auth plugin which shows the Android
// system's Google account picker INSIDE the app (no browser tab).
//
// PERSISTENCE: localStorage is unreliable on Capacitor Android — the
// WebView's storage can be wiped under memory pressure, on app updates, or
// after long inactivity. To survive across launches we ALSO write the
// session_token and uid via @capacitor/preferences (backed by Android's
// SharedPreferences, which is durable). On every app start we read
// Preferences first and restore them into localStorage so the synchronous
// `getOrCreateUserId()` helper sees them.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
import axios from "axios";
import { API, getOrCreateUserId } from "./api";

let _initialized = false;

// Resolved when rehydrateNativeSession() has finished (or immediately on web).
// The Lobby awaits this BEFORE calling /auth/me so it doesn't race the
// Authorization header. Without this gate, cold-start always shows
// "Session expired" briefly before the rehydrate completes.
let _rehydrateResolve;
export const rehydrateReady = new Promise((r) => { _rehydrateResolve = r; });
if (typeof window !== "undefined") {
  try {
    const o = window.location?.origin || "";
    const native = o.includes("localhost") || o.includes("127.0.0.1") || o.startsWith("capacitor:");
    if (!native && _rehydrateResolve) { _rehydrateResolve(); _rehydrateResolve = null; }
  } catch {}
}

function isNative() {
  // Consistent with api.js: we're "native" if our origin is localhost/127.0.0.1
  // (Capacitor WebView's local serve path) OR if Capacitor's bridge confirms it.
  try {
    if (typeof window !== "undefined" && window.location) {
      const origin = window.location.origin || "";
      if (origin.includes("localhost") || origin.includes("127.0.0.1") || origin.startsWith("capacitor:")) {
        return true;
      }
    }
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch { return false; }
}

// Tiny helper around @capacitor/preferences with safe no-op fallback on web.
async function _prefSet(key, value) {
  if (!isNative()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key, value: String(value || "") });
  } catch (e) { console.warn("[pref] set failed", key, e); }
}
async function _prefGet(key) {
  if (!isNative()) return null;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const r = await Preferences.get({ key });
    return r?.value || null;
  } catch { return null; }
}
async function _prefRemove(key) {
  if (!isNative()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.remove({ key });
  } catch {/* ignore */}
}

export async function initGoogleAuthNative() {
  if (!isNative() || _initialized) return;
  try {
    const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
    await GoogleAuth.initialize({
      scopes: ["profile", "email"],
      grantOfflineAccess: true,
    });
    _initialized = true;
  } catch (e) {
    console.warn("[google-auth-native] init failed:", e);
  }
}

/**
 * Trigger native Google sign-in.
 * @returns {Promise<{ ok: boolean, uid?: string, name?: string, picture?: string, bonus_amount?: number }>}
 */
export async function signInWithGoogleNative() {
  if (!isNative()) return { ok: false, reason: "not_native" };
  try {
    if (!_initialized) await initGoogleAuthNative();
    const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
    const user = await GoogleAuth.signIn();
    const idToken = user?.authentication?.idToken;
    if (!idToken) return { ok: false, reason: "no_id_token" };

    // Exchange the idToken for our own session token via the backend.
    const anonymousUserId = (() => { try { return getOrCreateUserId(); } catch { return null; } })();
    const res = await axios.post(`${API}/auth/google-native`, {
      id_token: idToken,
      anonymous_user_id: anonymousUserId,
    });
    const data = res.data || {};
    if (data.session_token) {
      // 1. Volatile cache for the current session (synchronous reads everywhere)
      try {
        localStorage.setItem("quattro_session_token", data.session_token);
        if (data.uid) localStorage.setItem("quattro_user_id", data.uid);
        localStorage.setItem("quattro_user_name", data.name || "");
        localStorage.setItem("quattro_authed", "1");
      } catch {}
      // 2. DURABLE backup via @capacitor/preferences. Survives WebView storage
      // wipes, low-memory kills, app updates, and other Android edge cases.
      await _prefSet("quattro_session_token", data.session_token);
      if (data.uid) await _prefSet("quattro_user_id", data.uid);
      await _prefSet("quattro_user_name", data.name || "");
      await _prefSet("quattro_authed", "1");
      // 3. Send the token as Bearer on every subsequent request.
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.session_token}`;
    }
    return {
      ok: true,
      uid: data.uid,
      name: data.name,
      email: data.email,
      picture: data.picture,
      bonus_amount: data.bonus_amount || 0,
    };
  } catch (e) {
    console.warn("[google-auth-native] sign-in failed:", e);
    return { ok: false, reason: e?.message || "error" };
  }
}

export async function signOutGoogleNative() {
  if (!isNative()) return;
  try {
    const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
    await GoogleAuth.signOut();
  } catch {/* ignore */}
  try {
    localStorage.removeItem("quattro_session_token");
    localStorage.removeItem("quattro_authed");
    localStorage.removeItem("quattro_user_id");
    localStorage.removeItem("quattro_user_name");
    delete axios.defaults.headers.common["Authorization"];
  } catch {}
  // Also remove the durable copies so a stale session can't auto-recover.
  await _prefRemove("quattro_session_token");
  await _prefRemove("quattro_authed");
  await _prefRemove("quattro_user_id");
  await _prefRemove("quattro_user_name");
}

/**
 * On app start, rehydrate the auth state. NOW ASYNC because we may need to
 * read from @capacitor/preferences (the durable native key-value store) when
 * localStorage has been wiped by the WebView. After this returns, the
 * `Authorization: Bearer ...` header is set and localStorage has the same
 * values so synchronous helpers (getOrCreateUserId, etc.) work.
 */
export async function rehydrateNativeSession() {
  if (!isNative()) {
    if (_rehydrateResolve) { _rehydrateResolve(); _rehydrateResolve = null; }
    return;
  }

  try {
    // 1. Check localStorage first (fast path — usually populated)
    let tok = null, uid = null, name = null, authed = null;
    try {
      tok = localStorage.getItem("quattro_session_token");
      uid = localStorage.getItem("quattro_user_id");
      name = localStorage.getItem("quattro_user_name");
      authed = localStorage.getItem("quattro_authed");
    } catch {}

    // 2. If localStorage is empty, fall back to @capacitor/preferences
    // (Android SharedPreferences). This survives storage wipes.
    if (!tok) {
      tok = await _prefGet("quattro_session_token");
      if (tok) {
        try { localStorage.setItem("quattro_session_token", tok); } catch {}
      }
    }
    if (!uid) {
      uid = await _prefGet("quattro_user_id");
      if (uid) { try { localStorage.setItem("quattro_user_id", uid); } catch {} }
    }
    if (!name) {
      name = await _prefGet("quattro_user_name");
      if (name) { try { localStorage.setItem("quattro_user_name", name); } catch {} }
    }
    if (!authed) {
      authed = await _prefGet("quattro_authed");
      if (authed) { try { localStorage.setItem("quattro_authed", authed); } catch {} }
    }
    if (!tok) return; // truly anonymous user

    axios.defaults.headers.common["Authorization"] = `Bearer ${tok}`;

    // 3. If uid is still missing or looks anon, ask the server who we are.
    const looksAnon = !uid || uid.startsWith("u_");
    if (!looksAnon) return;
    try {
      const res = await axios.post(`${API}/auth/recover-user-id`, {});
      const canonical = res.data?.user_id;
      if (canonical && canonical !== uid) {
        try {
          localStorage.setItem("quattro_user_id", canonical);
          localStorage.setItem("quattro_authed", "1");
        } catch {}
        await _prefSet("quattro_user_id", canonical);
        await _prefSet("quattro_authed", "1");
        if (typeof window !== "undefined" && window.location?.reload) {
          window.location.reload();
        }
      }
    } catch (e) {
      // 401 = session expired — wipe dead Bearer + storage so we don't loop
      if (e?.response?.status === 401) {
        try {
          localStorage.removeItem("quattro_session_token");
          localStorage.removeItem("quattro_authed");
        } catch {}
        await _prefRemove("quattro_session_token");
        await _prefRemove("quattro_authed");
        delete axios.defaults.headers.common["Authorization"];
      }
    }
  } finally {
    // ALWAYS resolve the gate so awaiters don't hang. The Lobby uses this to
    // wait for the Bearer header to be set before calling /auth/me.
    if (_rehydrateResolve) { _rehydrateResolve(); _rehydrateResolve = null; }
  }
}
